import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

// ---------------------------------------------------------------------------
// Regex patterns
// ---------------------------------------------------------------------------
const PATTERNS = {
  digits10: /^\d{0,10}$/,
  digitsOnly: /^\d*$/,
  textOnly: /^[a-zA-Z\s]*$/,
  alphanumeric: /^[a-zA-Z0-9\s]*$/,
  alphanumericDash: /^[a-zA-Z0-9\s\-_]*$/,
  email: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
  url: /^(https?:\/\/)?([\w-]+\.)+[\w-]{2,}(\/[\w-./?%&=]*)?$/,
  pan: /^[A-Z]{5}[0-9]{4}[A-Z]$/,
  aadhar: /^\d{12}$/,
  gst: /^\d{2}[A-Z]{5}\d{4}[A-Z]\d[Z][A-Z\d]$/,
  pincode: /^\d{6}$/,
  ifsc: /^[A-Z]{4}0[A-Z0-9]{6}$/,
  vehicleNumber: /^[A-Z]{2}\d{2}[A-Z]{1,2}\d{4}$/,
  password: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&#])[A-Za-z\d@$!%*?&#]{8,}$/,
};

// ---------------------------------------------------------------------------
// Individual validators — each returns an error string or empty string
// ---------------------------------------------------------------------------

export const validators = {

  required: (value, fieldLabel = 'This field') => {
    if (value === null || value === undefined || String(value).trim() === '') {
      return `${fieldLabel} is required`;
    }
    return '';
  },

  // Only digits, exactly 10 characters (Indian mobile)
  contact: (value) => {
    if (!value) return '';
    if (!/^\d*$/.test(value)) return 'Only digits are allowed';
    if (value.length !== 10) return 'Contact number must be exactly 10 digits';
    return '';
  },

  // Optional phone stored as varchar (+91, spaces, etc.)
  contactVarchar: (value, maxLen = 30) => {
    if (!value) return '';
    const trimmed = String(value).trim();
    if (trimmed.length > maxLen) return `Contact must be at most ${maxLen} characters`;
    if (!/^[\d\s+\-().]+$/.test(trimmed)) {
      return 'Contact can only contain digits, spaces, +, -, (), and .';
    }
    return '';
  },

  // Only digits with optional max length
  number: (value, { min, max, maxLength, label = 'Value' } = {}) => {
    if (!value && value !== 0) return '';
    if (!/^\d*\.?\d*$/.test(String(value))) return `${label} must be a valid number`;
    const num = Number(value);
    if (min !== undefined && num < min) return `${label} must be at least ${min}`;
    if (max !== undefined && num > max) return `${label} must be at most ${max}`;
    if (maxLength && String(value).length > maxLength) return `${label} must be at most ${maxLength} digits`;
    return '';
  },

  // Letters and spaces only
  textOnly: (value, label = 'This field') => {
    if (!value) return '';
    if (!PATTERNS.textOnly.test(value)) return `${label} should contain only letters and spaces`;
    return '';
  },

  // Letters, digits, spaces
  alphanumeric: (value, label = 'This field') => {
    if (!value) return '';
    if (!PATTERNS.alphanumeric.test(value)) return `${label} should not contain special characters`;
    return '';
  },

  // Letters, digits, spaces, hyphens, underscores
  alphanumericDash: (value, label = 'This field') => {
    if (!value) return '';
    if (!PATTERNS.alphanumericDash.test(value)) return `${label} can only contain letters, numbers, hyphens, and underscores`;
    return '';
  },

  email: (value) => {
    if (!value) return '';
    if (!PATTERNS.email.test(value)) return 'Please enter a valid email address';
    return '';
  },

  url: (value) => {
    if (!value) return '';
    if (!PATTERNS.url.test(value)) return 'Please enter a valid URL';
    return '';
  },

  minLength: (value, min, label = 'This field') => {
    if (!value) return '';
    if (String(value).length < min) return `${label} must be at least ${min} characters`;
    return '';
  },

  maxLength: (value, max, label = 'This field') => {
    if (!value) return '';
    if (String(value).length > max) return `${label} must be at most ${max} characters`;
    return '';
  },

  password: (value) => {
    if (!value) return '';
    if (value.length < 8) return 'Password must be at least 8 characters';
    if (!PATTERNS.password.test(value)) return 'Password must include uppercase, lowercase, number and special character';
    return '';
  },

  confirmPassword: (value, original) => {
    if (!value) return '';
    if (value !== original) return 'Passwords do not match';
    return '';
  },

  pan: (value) => {
    if (!value) return '';
    if (!PATTERNS.pan.test(value.toUpperCase())) return 'Invalid PAN format (e.g. ABCDE1234F)';
    return '';
  },

  aadhar: (value) => {
    if (!value) return '';
    const cleaned = value.replace(/\s/g, '');
    if (!PATTERNS.aadhar.test(cleaned)) return 'Aadhar number must be exactly 12 digits';
    return '';
  },

  gst: (value) => {
    if (!value) return '';
    if (!PATTERNS.gst.test(value.toUpperCase())) return 'Invalid GST number format';
    return '';
  },

  pincode: (value) => {
    if (!value) return '';
    if (!PATTERNS.pincode.test(value)) return 'Pincode must be exactly 6 digits';
    return '';
  },

  ifsc: (value) => {
    if (!value) return '';
    if (!PATTERNS.ifsc.test(value.toUpperCase())) return 'Invalid IFSC code format';
    return '';
  },

  vehicleNumber: (value) => {
    if (!value) return '';
    const cleaned = value.replace(/\s/g, '').toUpperCase();
    if (!PATTERNS.vehicleNumber.test(cleaned)) return 'Invalid vehicle number (e.g. MH12AB1234)';
    return '';
  },

  // File / image validators
  fileSize: (file, maxMB = 1) => {
    if (!file) return '';
    if (file.size > maxMB * 1024 * 1024) return `File size must be less than ${maxMB}MB`;
    return '';
  },

  fileType: (file, allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp']) => {
    if (!file) return '';
    if (!allowedTypes.includes(file.type)) {
      const names = allowedTypes.map(t => t.split('/')[1].toUpperCase()).join(', ');
      return `Allowed file types: ${names}`;
    }
    return '';
  },

  imageFile: (file, { maxMB = 2, types } = {}) => {
    if (!file) return '';
    const sizeErr = validators.fileSize(file, maxMB);
    if (sizeErr) return sizeErr;
    const typeErr = validators.fileType(file, types);
    if (typeErr) return typeErr;
    return '';
  },

  documentFile: (file, { maxMB = 5 } = {}) => {
    if (!file) return '';
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'application/pdf'];
    const sizeErr = validators.fileSize(file, maxMB);
    if (sizeErr) return sizeErr;
    const typeErr = validators.fileType(file, allowedTypes);
    if (typeErr) return typeErr;
    return '';
  },

  // Date: ensure value is not in the future
  pastDate: (value) => {
    if (!value) return '';
    if (new Date(value) > new Date()) return 'Date cannot be in the future';
    return '';
  },

  futureDate: (value) => {
    if (!value) return '';
    if (new Date(value) < new Date()) return 'Date cannot be in the past';
    return '';
  },

  select: (value, label = 'This field') => {
    if (!value || value === '') return `Please select a ${label}`;
    return '';
  },
};

// ---------------------------------------------------------------------------
// Input filters — restrict what can be typed (used in onChange)
// ---------------------------------------------------------------------------

export const inputFilters = {
  digitsOnly: (value) => value.replace(/\D/g, ''),
  digits10: (value) => value.replace(/\D/g, '').slice(0, 10),
  phoneVarchar: (value) => value.replace(/[^\d\s+\-().]/g, '').slice(0, 30),
  lettersOnly: (value) => value.replace(/[^a-zA-Z\s]/g, ''),
  alphanumeric: (value) => value.replace(/[^a-zA-Z0-9\s]/g, ''),
  alphanumericDash: (value) => value.replace(/[^a-zA-Z0-9\s\-_]/g, ''),
  uppercase: (value) => value.toUpperCase(),
  lowercase: (value) => value.toLowerCase(),
  noSpaces: (value) => value.replace(/\s/g, ''),
  singleSpaces: (value) => value.replace(/\s{2,}/g, ' '),
  pincode: (value) => value.replace(/\D/g, '').slice(0, 6),
  amount: (value) => {
    const cleaned = value.replace(/[^\d.]/g, '');
    const parts = cleaned.split('.');
    if (parts.length > 2) return parts[0] + '.' + parts.slice(1).join('');
    return cleaned;
  },
};

// ---------------------------------------------------------------------------
// useValidation — React hook for real-time field-level validation
// ---------------------------------------------------------------------------

/**
 * Usage:
 *
 *   const { errors, validate, validateField, clearField, clearAll, hasErrors } = useValidation();
 *
 *   // Define rules per field:
 *   const rules = {
 *     name:    (v) => validators.required(v, 'Name') || validators.textOnly(v, 'Name'),
 *     email:   (v) => validators.required(v, 'Email') || validators.email(v),
 *     contact: (v) => validators.required(v, 'Contact') || validators.contact(v),
 *   };
 *
 *   // On every input change (real-time):
 *   const handleChange = (e) => {
  const { t } = useTranslation();
 *     const { name, value } = e.target;
 *     setFormData(prev => ({ ...prev, [name]: value }));
 *     validateField(name, value, rules[name]);
 *   };
 *
 *   // On submit, validate everything at once:
 *   const handleSubmit = (e) => {
 *     e.preventDefault();
 *     if (!validate(formData, rules)) return;
 *     // proceed with API call …
 *   };
 *
 *   // In JSX:
 *   <input … />
 *   <ValidationMessage error={errors.name} />
 */
export function useValidation() {
  const [errors, setErrors] = useState({});

  const validateField = useCallback((fieldName, value, ruleFn) => {
    if (!ruleFn) return '';
    const rules = Array.isArray(ruleFn) ? ruleFn : [ruleFn];
    const error = rules.reduce((err, fn) => err || fn(value), '');
    setErrors(prev => {
      if (prev[fieldName] === error) return prev;
      return { ...prev, [fieldName]: error };
    });
    return error;
  }, []);

  const validate = useCallback((formData, rules) => {
    const newErrors = {};
    let isValid = true;
    for (const field of Object.keys(rules)) {
      const ruleFn = rules[field];
      const fieldRules = Array.isArray(ruleFn) ? ruleFn : [ruleFn];
      const error = fieldRules.reduce((err, fn) => err || fn(formData[field]), '');
      if (error) {
        newErrors[field] = error;
        isValid = false;
      } else {
        newErrors[field] = '';
      }
    }
    setErrors(newErrors);
    return isValid;
  }, []);

  const clearField = useCallback((fieldName) => {
    setErrors(prev => ({ ...prev, [fieldName]: '' }));
  }, []);

  const clearAll = useCallback(() => setErrors({}), []);

  const hasErrors = Object.values(errors).some(Boolean);

  return { errors, validate, validateField, clearField, clearAll, hasErrors };
}

// ---------------------------------------------------------------------------
// ValidationMessage — small inline warning component
// ---------------------------------------------------------------------------

export function ValidationMessage({ error, className = '' }) {
  if (!error) return null;
  return (
    <p className={`text-xs text-red-600 mt-1 flex items-center gap-1 animate-fadeIn ${className}`}>
      <svg xmlns="http://www.w3.org/2000/svg" className="w-3 h-3 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
      </svg>
      {error}
    </p>
  );
}

export default validators;
